import {isSelectiveAiDomains} from 'common/HelperFunctions'
import Spinner from 'common/components/Spinner'
import {GENERAL_ENABLE_DISABLE, SUBJECTS_STATUS} from 'common/constants'
import {isFeedbackDisabled} from 'common/helpers'
import {getOnboardingSampleDataPrefs} from 'common/helpers/onboardingSampleData'
import {useEffect, useLayoutEffect, useMemo, useState} from 'react'
import {shallowEqual, useDispatch, useSelector} from 'react-redux'
import {Outlet, useLocation} from 'react-router'
import {toast} from 'react-toastify'
import {RootState, UserReducer} from 'setup'
import {
  attendanceSettingCreate,
  attendanceSettingsByBranchIds,
} from 'setup/redux/Actions/Attendance/attendance'
import {getClasses} from 'setup/redux/Actions/Class/class'
import {getMappings} from 'setup/redux/Actions/CustomNames/customNames'
import {getUserPreferences} from 'setup/redux/Actions/Dashboard/dashboard'
import {getGeneralSettings} from 'setup/redux/Actions/General/settings'
import {getLevels} from 'setup/redux/Actions/Level/level'
import {getGrades} from 'setup/redux/Actions/School/Grade/grade'
import {getSections} from 'setup/redux/Actions/School/Section/section'
import {getStudentList} from 'setup/redux/Actions/School/Student/student'
import {getTeacherList} from 'setup/redux/Actions/School/Teacher/teacher'
import {getLoggedInUserData} from 'setup/redux/Actions/SignIn/login'
import {getSubjects} from 'setup/redux/Actions/Subject/subject'
import {getTimetableListData} from 'setup/redux/Actions/Timetable/timetable'

import FeedbackSurveyPopup from '../../app/modules/feedback/FeedbackSurveyPopup'
import {reInitMenu} from '../helpers'
import {systemMode, useThemeMode} from '../partials/layout/theme-mode/ThemeModeProvider'
import {Content} from './components/content'
import {FooterWrapper} from './components/footer'
import {HeaderWrapper} from './components/header'
import {SampleDataAlert} from './components/header/SampleDataAlert'
import {SupportStaffAlert} from './components/header/SupportStaffAlert'
import {ScrollTop} from './components/scroll-top'
import {Sidebar} from './components/sidebar'
import {ToolbarWrapper} from './components/toolbar'
import {PageDataProvider} from './core'

const DASHBOARD_GRADIENT = 'linear-gradient(90.14deg, #0D0E12 49.99%, #431214 99.87%)'

const MasterLayout = () => {
  const location = useLocation()
  const dispatch = useDispatch()
  const {mode} = useThemeMode()
  const isDark = mode === 'dark' || (mode === 'system' && systemMode === 'dark')

  const showDashboardGradient = useMemo(() => {
    const path = location.pathname
    const isDashboard = path === '/dashboard' || path === '/institute/dashboard'
    const step = new URLSearchParams(location.search).get('step')
    const hasStep0 = step === '0'
    return isDashboard && hasStep0 && isSelectiveAiDomains() && isDark
  }, [location.pathname, location.search, isDark])

  const userReducer: UserReducer = useSelector<RootState>(
    ({userReducer}) => userReducer,
    shallowEqual
  ) as UserReducer

  const accessToken: string = userReducer.accessToken as string

  const userPreferences = useSelector<RootState>(
    ({mainReducer}) => mainReducer.userPreferences,
    shallowEqual
  ) as Record<string, unknown> | null | undefined

  const [loading, setLoading] = useState(!!accessToken)

  useLayoutEffect(() => {
    if (accessToken) {
      setLoading(true)
      const onSuccess = () => {
        setLoading(false)
        reInitMenu()
      }

      const onFailure = (err: any) => {
        const message =
          err?.response?.data?.message ??
          err?.response?.message ??
          err?.message ??
          'Invalid Auth Token'
        const is401 = err?.statusCode === 401
        const looksUnauthorized = is401 || /unauthorized/i.test(String(message))
        if (!(isSelectiveAiDomains() && looksUnauthorized)) {
          toast.error(message)
        }
        setLoading(false)
      }

      dispatch(getLoggedInUserData(onSuccess, onFailure))
      dispatch(getGeneralSettings())
      if (isSelectiveAiDomains()) {
        dispatch(getUserPreferences())
      }
      dispatch(getClasses({}, null, null, true))
      dispatch(getMappings())
      dispatch(attendanceSettingsByBranchIds())
      dispatch(getSubjects({search: {status: SUBJECTS_STATUS.ACTIVE}}, null, null, true))
      dispatch(getLevels())
      dispatch(
        getGrades(
          {search: {status: GENERAL_ENABLE_DISABLE.ENABLE}},
          () => {},
          () => {}
        )
      )
      if (isSelectiveAiDomains()) {
        const listBody = {page: 1, pageSize: 10000, sort: {}, search: {}}
        dispatch(getTeacherList(listBody, null, null, true) as never)
        dispatch(
          getStudentList(
            {...listBody, branchId: localStorage.getItem('branch')},
            null,
            null,
            true
          ) as never
        )
        dispatch(getSections(listBody, null, null, true) as never)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!isSelectiveAiDomains()) return
    const samplePrefs = getOnboardingSampleDataPrefs(userPreferences)
    if (!samplePrefs?.active) return
    const levelId = samplePrefs.levelId
    if (typeof levelId !== 'number' || levelId <= 0) return
    dispatch(getTimetableListData(levelId, null, null, true) as never)
  }, [dispatch, userPreferences])

  useEffect(() => {
    reInitMenu()
  }, [location.key])

  const attendance_setting = useSelector<RootState, any>(
    (state) => state.mainReducer.attendance_setting,
    shallowEqual
  )

  useEffect(() => {
    if (Array.isArray(attendance_setting) && attendance_setting.length === 0) {
      const onSuccess = () => {}
      const onFailure = () => {}
      dispatch(attendanceSettingCreate(undefined, onSuccess, onFailure))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attendance_setting])

  useEffect(() => {
    if (!loading && isSelectiveAiDomains()) {
      document.body.classList.add('app-ready')
    }
  }, [loading])

  if (loading) {
    if (isSelectiveAiDomains()) {
      // HTML splash screen handles the loader for selective AI domains.
      return null
    }
    return (
      <div id='splash-screen' className='splash-screen bg-body'>
        <Spinner type='grow' size='lg' className='mb-4' />
        <div className='text-gray-800'>Loading ...</div>
      </div>
    )
  }

  return (
    <PageDataProvider>
      <div
        className='d-flex flex-column flex-root app-root'
        id='kt_app_root'
        data-selective-ai-domain={isSelectiveAiDomains() ? 'true' : undefined}
      >
        {/* Selective-AI domains render this once at the App root instead (App.tsx). */}
        {!isSelectiveAiDomains() && <SupportStaffAlert />}
        <div
          className='app-page flex-column flex-column-fluid'
          id='kt_app_page'
          data-kt-dashboard-gradient={showDashboardGradient ? 'true' : undefined}
          style={
            showDashboardGradient ? {background: DASHBOARD_GRADIENT, minHeight: '100vh'} : undefined
          }
        >
          <HeaderWrapper />
          <div className='app-wrapper flex-column flex-row-fluid' id='kt_app_wrapper'>
            <Sidebar />
            <div className='app-main flex-column flex-row-fluid' id='kt_app_main'>
              <div className='d-flex flex-column flex-column-fluid'>
                <SampleDataAlert />
                <ToolbarWrapper />
                <Content>
                  <Outlet />
                </Content>
              </div>
              <FooterWrapper />
            </div>
          </div>
        </div>
      </div>

      {/* begin:: Drawers */}
      {/* <ActivityDrawer /> */}
      {/* <RightToolbar /> */}
      {/* <DrawerMessenger /> */}
      {/* end:: Drawers */}

      {/* begin:: Modals */}
      {/* <InviteUsers /> */}
      {/* <UpgradePlan /> */}
      {/* end:: Modals */}

      <ScrollTop />
      {!isFeedbackDisabled() && !isSelectiveAiDomains() && <FeedbackSurveyPopup />}
    </PageDataProvider>
  )
}

export {MasterLayout}
